import { useState, useMemo } from 'react'
import { BookOpen, Search } from 'lucide-react'

import { Button } from '@/components'

type Course = {
  id: string
  title: string
  description: string
  duration: string
  students: string
  rating: number
  price: string
}

const COURSES: Course[] = [
  {
    id: '1',
    title: 'Introduction to Robotics',
    description:
      'Learn the fundamentals of robotics, including sensors, motion, and basic programming with hands-on projects.',
    duration: '8 weeks',
    students: '1,234 students',
    rating: 4.8,
    price: '$299',
  },
  {
    id: '2',
    title: 'Advanced Robotics & AI',
    description:
      'Build more advanced robotic concepts including AI decision-making, machine learning for robots, and autonomous systems.',
    duration: '10 weeks',
    students: '985 students',
    rating: 4.9,
    price: '$899',
  },
  {
    id: '3',
    title: 'Web Development',
    description:
      'Build your first website. Learn HTML, CSS, and JavaScript through fun, interactive projects.',
    duration: '10 weeks',
    students: '2,341 students',
    rating: 4.8,
    price: '$249',
  },
  {
    id: '4',
    title: 'Scratch Game Design',
    description:
      'Design and build games in Scratch while learning core programming concepts like loops and conditionals.',
    duration: '6 weeks',
    students: '1,120 students',
    rating: 4.7,
    price: '$199',
  },
] as const

export const ParentCourses: React.FC = () => {
  const [query, setQuery] = useState('')

  const filteredCourses = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return COURSES
    return COURSES.filter(course => {
      return (
        course.title.toLowerCase().includes(q) ||
        course.description.toLowerCase().includes(q)
      )
    })
  }, [query])

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold text-gray-900">Browse Courses</h1>
        <p className="text-sm text-gray-500">
          Explore learning opportunities for your child.
        </p>
      </div>

      <div className="rounded-2xl bg-white shadow-sm border border-gray-100 px-4 py-3 sm:px-5 sm:py-4">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.currentTarget.value)}
              placeholder="Search courses"
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-9 py-2.5 text-sm text-gray-900 shadow-sm outline-none transition focus:border-portal-orange focus:bg-white focus:ring-1 focus:ring-portal-orange/40"
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            className="hidden whitespace-nowrap rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 sm:inline-flex"
          >
            Filters
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>
          Showing{' '}
          <span className="font-semibold">{filteredCourses.length}</span>{' '}
          {filteredCourses.length === 1 ? 'course' : 'courses'}
        </span>
      </div>

      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {filteredCourses.map(course => (
          <article
            key={course.id}
            className="group flex h-full flex-col overflow-hidden rounded-2xl bg-white shadow-[0_14px_30px_rgba(15,23,42,0.12)]"
          >
            <div className="flex items-center justify-center bg-[#F28C28] px-6 py-8 text-white">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10">
                <BookOpen className="h-7 w-7" />
              </div>
            </div>
            <div className="flex flex-1 flex-col gap-3 border-t border-gray-100 bg-white px-5 py-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  {course.title}
                </h3>
                <p className="mt-1 text-xs text-gray-500 line-clamp-3">
                  {course.description}
                </p>
              </div>

              <div className="mt-auto space-y-1 text-[11px] text-gray-500">
                <p>
                  <span className="font-medium text-gray-700">
                    {course.duration}
                  </span>{' '}
                  · {course.students}
                </p>
              </div>

              <div className="mt-2 flex items-center justify-between border-t border-gray-100 pt-3">
                <div className="flex items-center gap-1 text-xs text-gray-700">
                  <span className="text-amber-500">★</span>
                  <span className="font-semibold">{course.rating}</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">
                  {course.price}
                </span>
              </div>
            </div>
          </article>
        ))}

        {filteredCourses.length === 0 && (
          <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-6 py-10 text-center text-sm text-gray-500">
            No courses match your search yet.
          </div>
        )}
      </div>
    </div>
  )
}
